// AI Chat Service - Smart context-aware assistant with streaming
import { supabase } from '../lib/supabase';

const API_BASE = window.location.origin;

// Gather real platform data for AI context
async function gatherAppContext(userId) {
  const ctx = {};
  try {
    // User profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('name, headline, department, role, institution, about, research_interests, skills')
      .eq('id', userId)
      .maybeSingle();
    if (profile) ctx.user = profile;

    // User stats
    const { count: connectionCount } = await supabase
      .from('connections')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'accepted')
      .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
    ctx.stats = { connections: connectionCount || 0 };

    // Recent posts (latest 8 from platform)
    const { data: posts } = await supabase
      .from('posts')
      .select('content, type, created_at, author:profiles!author_id(name, department), likes:post_likes(count), comments:post_comments(count)')
      .order('created_at', { ascending: false })
      .limit(8);
    if (posts?.length) {
      ctx.recentPosts = posts.map(p => ({
        content: (p.content || '').substring(0, 150),
        type: p.type,
        authorName: p.author?.name,
        authorDept: p.author?.department,
        likeCount: p.likes?.[0]?.count || 0,
        commentCount: p.comments?.[0]?.count || 0,
      }));
    }

    // User's connections (top 10)
    const { data: conns } = await supabase
      .from('connections')
      .select('requester:profiles!requester_id(name, department), addressee:profiles!addressee_id(name, department), requester_id, addressee_id')
      .eq('status', 'accepted')
      .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`)
      .limit(10);
    if (conns?.length) {
      ctx.connections = conns.map(c => {
        const other = c.requester_id === userId ? c.addressee : c.requester;
        return { name: other?.name, department: other?.department };
      });
    }

    // Communities
    const { data: communities } = await supabase
      .from('communities')
      .select('name, description, members:community_members(count)')
      .order('created_at', { ascending: false })
      .limit(5);
    if (communities?.length) {
      ctx.communities = communities.map(c => ({ name: c.name, memberCount: c.members?.[0]?.count || 0 }));
    }

    // Trending topics
    const { data: topics } = await supabase
      .from('trending_topics')
      .select('tag, post_count')
      .order('post_count', { ascending: false })
      .limit(5);
    if (topics?.length) ctx.trendingTopics = topics;

  } catch (e) {
    console.warn('Error gathering AI context:', e.message);
  }
  return ctx;
}

// Send message with streaming support
export async function sendAIMessage(messages, userId) {
  try {
    // Gather real app context
    const appContext = userId ? await gatherAppContext(userId) : {};

    const res = await fetch(`${API_BASE}/api/ai/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages, appContext }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'AI service error');
    }

    const contentType = res.headers.get('content-type') || '';

    // Handle streaming response (SSE)
    if (contentType.includes('text/event-stream')) {
      return { stream: true, body: res.body };
    }

    // Handle non-streaming response (fallback)
    const data = await res.json();
    return { stream: false, reply: data.reply };
  } catch (err) {
    console.error('AI service error:', err);
    return { stream: false, reply: getFallbackResponse(messages[messages.length - 1]?.content || '') };
  }
}

// Parse SSE stream and yield text chunks
export async function* parseSSEStream(body) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data === '[DONE]') return;
          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) yield content;
          } catch {
            // Skip malformed chunks
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function getFallbackResponse(message) {
  const lower = message.toLowerCase();
  if (lower.includes('hello') || lower.includes('hi') || lower.includes('hey')) {
    return "Hello! 👋 I'm your Campus AI Assistant. I can help you navigate the platform, find research resources, connect with peers, and more. What would you like to know?";
  }
  if (lower.includes('research') || lower.includes('paper')) {
    return "I can help with research! Use the search bar to find topics, browse trending hashtags, or check community discussions for paper recommendations.";
  }
  if (lower.includes('connect') || lower.includes('network')) {
    return "Head to the Network page to discover researchers in your field. Send connection requests and start meaningful academic collaborations!";
  }
  if (lower.includes('community') || lower.includes('group')) {
    return "Check out the Communities section to find groups in your field. You can join discussions, share papers, and collaborate with peers!";
  }
  if (lower.includes('help')) {
    return "I can assist with:\n• Finding research papers & resources\n• Navigating campus communities\n• Connecting with researchers\n• Summarizing recent platform activity\n• Writing and career tips\n\nJust ask me anything!";
  }
  return "That's a great question! I suggest checking the Communities section for discussions on this topic, or use the search bar to find related posts and researchers.";
}
